import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ChevronRight, Minus, Plus } from "lucide-react";
import { Box, CircularProgress, TablePagination } from "@mui/material";
import styles from "./demandTable.module.css";
import { downloadXls } from "../utils/exportToXls";
import StickyScrollShell from "../StickyScrollShell";
import { fetchBGData, fetchDemandPageSite } from "Samarth Module/api/BGDataApi";
import { AiOutlineConsoleSql } from "react-icons/ai";

const fullFmt = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
});
const formatNumber = (value) => {
    if (value === undefined || value === null) return "";
    const num = Number(value);
    if (!Number.isFinite(num)) return "";
    if (num === 0) return "0";
    return num < 10 ? num.toFixed(2) : fullFmt.format(num);
};

const formatForExport = (value) => {
    if (value === undefined || value === null) return 0;
    const num = Number(value);
    if (!Number.isFinite(num)) return 0;
    return Math.round(num);
};

const WEEK_NUMBER_PATTERN = /\d+/;
const WEEK_KEY_PATTERN = /(?:demand)?week(\d+)/;

const weekNumber = (key) => {
    const match = WEEK_NUMBER_PATTERN.exec(String(key));
    return match ? Number(match[0]) : 0;
};

const weekLabel = (key) => `Week ${weekNumber(key)}`;

const deriveWeeks = (weeklyTotal) =>
    Object.keys(weeklyTotal || {}).sort((a, b) => weekNumber(a) - weekNumber(b));

const clusterInfoOf = (row) => {
    const cluster = row.SiteCluster;
    return (Array.isArray(cluster) ? cluster[0] : cluster) || null;
};

const primaryLabelFor = (row, siteMode) => {
    const level = canonicalLevel(row.level);
    const clusterInfo = clusterInfoOf(row);
    const clusterName = clusterInfo?.name || "";
    const siteCode = row.SiteCode?.name || "";
    if (level === "BG") {
        if (siteMode == null) return clusterName || row.label;
        return (siteMode ? siteCode : clusterName) || row.label;
    }
    if (level === "SITE") return siteCode || row.label;
    return row.label;
};

const secondaryLabelFor = (row) => {
    if (canonicalLevel(row.level) !== "BG") return "";
    const clusterInfo = clusterInfoOf(row);
    return clusterInfo?.siteCount == null ? "" : `${clusterInfo.siteCount} sites`;
};

const toNode = (row, expansionOptions = [], chainIndex = 0, siteMode = null, parentPath = null, parentCbu = null) => {
    const exp = row.expandable;
    const clusterInfo = clusterInfoOf(row);
    const clusterName = clusterInfo?.name || "";
    const siteCode = row.SiteCode?.name || "";
    const rowLevel = canonicalLevel(row.level) === "SITE" && !exp?.sites && exp?.materials ? row.curr_level : row.level;
    const canonicalRowLevel = canonicalLevel(rowLevel);
    const cbu = canonicalRowLevel === "CBU" ? (row.label || null) : parentCbu;
    
    return {
        id: row.id,
        path: parentPath ? `${parentPath}>${row.id}` : String(row.id),
        level: rowLevel,
        label: row.label,
        clusterName,
        siteCode,
        uom: row.UOM || "",
        des:row.Description || "",
        primary: primaryLabelFor(row, siteMode),
        secondary: secondaryLabelFor(row),
        weeklyData: row.weeklyData || {},
        canMaterials: exp ? !!exp.materials : expansionOptions.includes("MATERIALS"),
        canSites: exp ? !!exp.sites : expansionOptions.includes("SITES"),
        nextMaterialLevel: exp?.nextMaterialLevel || null,
        chainIndex,
        cbu,
    };
};

// Named helper (instead of an inline arrow inside a callback) so callers
// stay within the max nested-function-depth limit.
const rowsToNodes = (rows, expansionOptions, chainIndex, siteMode, parentPath, parentCbu = null) =>
    (rows || []).map((row) => toNode(row, expansionOptions, chainIndex, siteMode, parentPath, parentCbu));

const LEVEL_OVERRIDES = {
    smallc: "SmallC",
    product: "CBU",
    component: "RMPM",
};
const canonicalLevel = (level) => {
    const raw = String(level || "");
    return LEVEL_OVERRIDES[raw.toLowerCase()] || raw.toUpperCase();
};

const normLevel = (level) => String(level || "").replaceAll(/\s+/g, "").toUpperCase();
const buildLevelChain = (sublevelFilters) => {
    const chain = [];
    (sublevelFilters || []).forEach((lvl) => {
        if (!chain.some((existing) => normLevel(existing) === normLevel(lvl))) chain.push(lvl);
    });
    return chain;
};

const levelIndexInChain = (nodeLevel, levelChain) =>
    levelChain.findIndex((level) => normLevel(level) === normLevel(nodeLevel));

const resolveCurrentChainLevel = (node, levelChain) => {
    if (!levelChain.length) return null;
    const index = levelIndexInChain(canonicalLevel(node.level), levelChain);
    if (index >= 0) return levelChain[index];
    return canonicalLevel(node.level) === "BG" ? null : (levelChain[node.chainIndex ?? 0] || null);
};

const resolveNextChainLevel = (node, levelChain) => {
    if (!levelChain.length) return null;
    const index = levelIndexInChain(canonicalLevel(node.level), levelChain);
    if (index >= 0) return levelChain[index + 1] || null;
    return canonicalLevel(node.level) === "BG" ? (levelChain[0] || null) : (levelChain[(node.chainIndex ?? 0) + 1] || null);
};

const resolveParentLevel = (node, levelChain) => {
    if (levelChain.length) return resolveCurrentChainLevel(node, levelChain) || "BG";
    const isSiteRow = canonicalLevel(node.level) === "SITE";
    return isSiteRow ? "BG" : canonicalLevel(node.level);
};

const parentRef = (node, nextLevel, levelChain = []) => {
    const expandedSiteCode = !!node.siteCode;
    const level = resolveParentLevel(node, levelChain);
    const parent = {
        id: node.label,
        level,
    };
    const siteClusterId = expandedSiteCode ? node.siteCode : node.clusterName;
    if (siteClusterId) parent.siteClusterId = siteClusterId;
    if (nextLevel) parent.nextLevel = nextLevel;
    if (canonicalLevel(node.level) === "RMPM" && node.cbu) {
        parent.cbu = node.cbu;
    }
    return { parent, expandedSiteCode };
};

const siteModeParentRef = (node, nextLevel, siteMode) => {
    const parent = {
        id: node.label,
        level: canonicalLevel(node.level),
    };
    const siteClusterId = siteMode ? node.siteCode : node.clusterName;
    if (siteClusterId) parent.siteClusterId = siteClusterId;
    if (nextLevel) parent.nextLevel = nextLevel;
    if (normLevel(nextLevel) === "RMPM" && node.cbu) {
        parent.cbu = node.cbu;
    }
    return parent;
};

let exportCache = {
    weeks: [], nodes: [], weeklyTotal: {}, filters: {}, sublevelFilters: [], totalRecords: 0, showSiteCode: null,
};
const PAGE_SIZE = 25;

const fetchPagesInBatches = async (totalPages, fetchPage, batchSize = 5) => {
    const pages = [];
    for (let firstPage = 1; firstPage <= totalPages; firstPage += batchSize) {
        const lastPage = Math.min(firstPage + batchSize - 1, totalPages);
        const batch = Array.from({ length: lastPage - firstPage + 1 }, (_, index) => fetchPage(firstPage + index));
        pages.push(...await Promise.all(batch));
    }
    return pages;
};

const fetchAllBGPages = async (params, fetchFn = fetchBGData) => {
    const firstPage = await fetchFn({ ...params, pagination: { page: 1, pageSize: PAGE_SIZE }, UOM: params.UOM === 'Default' ? 'EA' : params.UOM });
    const totalPages = Math.max(1, Math.ceil(firstPage.totalRecords / PAGE_SIZE));
    if (totalPages === 1) return [firstPage];
    const remainingPages = await fetchPagesInBatches(totalPages - 1, (index) => fetchFn({
        ...params,
        pagination: { page: index + 1, pageSize: PAGE_SIZE },
        UOM: params.UOM === 'Default' ? 'EA' : params.UOM,
    }));
    return [firstPage, ...remainingPages];
};

const childKey = (nodeId, direction) => `${nodeId}|${direction}`;

const BG_COL_FONT = "400 13px 'UnileverShilling', sans-serif";
let measureCanvas = null;
const measureTextWidth = (text, ready) => {
    if (!ready || typeof document === "undefined") return null;
    if (!measureCanvas) measureCanvas = document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    if (!ctx) return null;
    ctx.font = BG_COL_FONT;
    return ctx.measureText(String(text || "")).width;
};

// Walks the currently-expanded rows (top level plus any expanded material/site
// children) to figure out how wide the sticky "BG" column needs to be.
const widthOfNode = (node, depth, expandedMat, expandedSites, childData, fontsReady) => {
    const CHAR_PX = 8;
    const BASE = 72;
    const str = String(node.primary || node.label || "");
    const measured = measureTextWidth(str, fontsReady);
    const textWidth = measured == null ? str.length * CHAR_PX : measured;
    let need = BASE + depth * 14 + textWidth;

    const matEntry = childData[childKey(node.path, "MATERIALS")];
    if (expandedMat[node.path] && matEntry?.rows) {
        matEntry.rows.forEach((child) => {
            need = Math.max(need, widthOfNode(child, depth + 1, expandedMat, expandedSites, childData, fontsReady));
        });
    }
    const siteEntry = childData[childKey(node.path, "SITES")];
    if (expandedSites[node.path] && siteEntry?.rows) {
        siteEntry.rows.forEach((child) => {
            need = Math.max(need, widthOfNode(child, depth + 1, expandedMat, expandedSites, childData, fontsReady));
        });
    }
    return need;
};

const computeBgColumnWidth = (nodes, expandedMat, expandedSites, childData, fontsReady) => {
    const MIN = 170;
    const MAX = 520;
    const need = nodes.reduce(
        (max, node) => Math.max(max, widthOfNode(node, 0, expandedMat, expandedSites, childData, fontsReady)),
        MIN
    );
    return Math.min(MAX, Math.round(need));
};

const normalizedColumnKey = (key) => {
    const normalized = String(key || "").replaceAll(/[\s_-]+/g, "").toLowerCase();
    if (["bg", "businessgroup"].includes(normalized)) return "bg";
    if (["site", "sites", "sitecode"].includes(normalized)) return "site";
    if (normalized === "demand") return "demand";
    const week = WEEK_KEY_PATTERN.exec(normalized);
    return week ? `week${week[1]}` : normalized;
};

const FIXED_COLUMN_LABEL = { bg: "BG", site: "SITE", uom: "UOM" };
const FIXED_COLUMN_WIDTH = { site: 170, uom: 90 };
const STICKY_TD_CLASSES = [styles.stickyCol1, styles.stickyCol2, styles.stickyCol3];
const STICKY_TH_CLASSES = [styles.stickyHeaderCol1, styles.stickyHeaderCol2, styles.stickyHeaderCol3];
const widthOfFixedColumn = (key, bgColWidth) => (key === "bg" ? bgColWidth : FIXED_COLUMN_WIDTH[key]);

// Derives which columns/weeks are visible and in what order, based on the
// column preferences passed in from the parent. Kept outside the component so
// the main component body stays focused on data-fetching and rendering.
const getColumnVisibility = (columnPreferences, weeks) => {
    const preferenceFor = (key) => columnPreferences.find((column) => (
        normalizedColumnKey(column.key) === normalizedColumnKey(key)
        || normalizedColumnKey(column.label) === normalizedColumnKey(key)
    ));
    const isColumnVisible = (key, fallback = true) => {
        const preference = preferenceFor(key);
        return preference ? preference.visibility !== false && preference.checked !== false : fallback;
    };
    const displayOrderFor = (key) => preferenceFor(key)?.DisplayOrder ?? Number.MAX_SAFE_INTEGER;
    const weekDisplayOrder = (week) =>
        preferenceFor(week)?.DisplayOrder ?? preferenceFor(weekLabel(week))?.DisplayOrder ?? Number.MAX_SAFE_INTEGER;

    const showBg = isColumnVisible("bg");
    const showSites = isColumnVisible("site");
    const showUom = isColumnVisible("uom");
    const showDemand = isColumnVisible("demand");
    const fixedColumnOrder = [showBg && "bg", showSites && "site", showUom && "uom"]
        .filter(Boolean)
        .sort((a, b) => displayOrderFor(a) - displayOrderFor(b));
    const visibleWeeks = showDemand
        ? weeks
            .filter((week) => isColumnVisible(weekLabel(week)))
            .sort((a, b) => weekDisplayOrder(a) - weekDisplayOrder(b))
        : [];

    return { showDemand, fixedColumnOrder, visibleWeeks };
};

// Derives the sticky-column widths used throughout the table. Kept outside
// the component so it doesn't add to DemandTable's complexity.
const computeStickyLayout = (fixedColumnOrder, bgColWidth, visibleWeeks) => {
    const [col1Width = 0, col2Width = 0, col3Width = 0] = fixedColumnOrder.map(
        (key) => widthOfFixedColumn(key, bgColWidth)
    );
    const frozenWidth = col1Width + col2Width + col3Width;
    const tableMinWidth = frozenWidth + (visibleWeeks.length * 110);
    return { col1Width, col2Width, col3Width, frozenWidth, tableMinWidth };
};

// Renders one <td>/<th> per visible fixed column, in the user's chosen order,
// using whichever sticky-slot class matches its position.
const renderFixedCells = (fixedColumnOrder, stickyClasses, cellFor) => (
    fixedColumnOrder.map((key, slotIndex) => cellFor(key, stickyClasses[slotIndex]))
);

// Encapsulates fetching (and re-fetching on filter/page change) of the top
// level BG/site rows, plus the expansion state used to drive drill-downs.
const useDemandData = ({ filtersKey, sublevelKey, clearFiltersKey, clearFilters, showSiteCode, customizationKey, uom, columnPreferencesReady }) => {
    const [apiPayload, setApiPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [expandedMat, setExpandedMat] = useState({});
    const [expandedSites, setExpandedSites] = useState({});
    const [childData, setChildData] = useState({});

    useEffect(() => {
        setPage(1);
    }, [filtersKey, sublevelKey, clearFiltersKey, showSiteCode, customizationKey]);

    useEffect(() => {
        if (!columnPreferencesReady) {
            setLoading(true);
            return undefined;
        }
        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError("");
        setExpandedMat({});
        setExpandedSites({});
        setChildData({});

        const request = showSiteCode == null
            ? fetchBGData({
                filters: JSON.parse(filtersKey),
                sublevelFilters: JSON.parse(sublevelKey),
                clearFilters,
                pagination: { page, pageSize: PAGE_SIZE },
                signal: controller.signal,
                UOM: uom === 'Default' ?  "EA" : uom,
            })
            : fetchDemandPageSite({
                filters: JSON.parse(filtersKey),
                sublevelFilters: JSON.parse(sublevelKey),
                pagination: { page, pageSize: PAGE_SIZE },
                parent: null,
                showSiteCode,
                signal: controller.signal,
                UOM: uom === 'Default' ?  "EA" : uom,
            });

        request
            .then((data) => {
                if (!cancelled) setApiPayload(data);
            })
            .catch((err) => {
                if (cancelled || err?.name === "AbortError") return;
                setError("Unable to load demand data. Please try again.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [filtersKey, sublevelKey, clearFiltersKey, page, showSiteCode, customizationKey, uom, columnPreferencesReady]);

    return {
        apiPayload, loading, error,
        page, setPage,
        expandedMat, setExpandedMat,
        expandedSites, setExpandedSites,
        childData, setChildData,
    };
};

// Encapsulates building & issuing the drill-down (materials/sites) request
// and the expand/collapse toggles that trigger it. Pulled out of the
// component so its branching doesn't count toward DemandTable's complexity.
const useChildExpansion = ({
    filtersKey, sublevelKey, clearFilters, showSiteCode, levelChain,
    expandedMat, setExpandedMat, expandedSites, setExpandedSites,
    childData, setChildData, uom,
}) => {
    const materialNextLevel = (node) =>
    (levelChain.length
        ? resolveNextChainLevel(node, levelChain)
        : (node.nextMaterialLevel || null));

    const siteNextLevel = (node) =>
        (levelChain.length ? (resolveCurrentChainLevel(node, levelChain) || null) : null);

    const buildChildRequest = (node, direction, nextLevel) => {
        if (showSiteCode == null) {
            const { parent, expandedSiteCode } = parentRef(node, nextLevel, levelChain);
            return fetchBGData({
                filters: JSON.parse(filtersKey),
                sublevelFilters: JSON.parse(sublevelKey),
                clearFilters,
                parent,
                expandDirection: direction,
                expandedSiteCode,
                UOM: uom === 'Default' ? 'EA' : uom,
            });
        }
        return fetchDemandPageSite({
            filters: JSON.parse(filtersKey),
            sublevelFilters: JSON.parse(sublevelKey),
            parent: siteModeParentRef(node, nextLevel, showSiteCode),
            showSiteCode,
            UOM: uom === 'Default' ? 'EA' : uom,
        });
    };

    const ensureChildren = (node, direction) => {
        const key = childKey(node.path, direction);
        const existing = childData[key];
        if (existing && (existing.loading || existing.rows)) return;
        setChildData((prev) => ({ ...prev, [key]: { loading: true, error: "", rows: null } }));

        const isMaterials = direction === "MATERIALS";
        const nextLevel = isMaterials ? materialNextLevel(node) : siteNextLevel(node);
        const childIndex = isMaterials
            ? (node.chainIndex ?? 0) + 1
            : (node.chainIndex ?? 0);
        const childCbu = canonicalLevel(node.level) === "CBU" ? node.label : node.cbu;

        buildChildRequest(node, direction, nextLevel)
            .then((data) => {
                const opts = data.expansionOptions?.length ? data.expansionOptions : ["SITES", "MATERIALS"];
                setChildData((prev) => ({
                    ...prev,
                    [key]: {
                        loading: false,
                        error: "",
                        rows: rowsToNodes(data.rows, opts, childIndex, showSiteCode, node.path, childCbu),
                    },
                }));
            })
            .catch((err) => {
                console.error(
                    `BG drill-down failed for parent=${node.id} (level=${node.level}, dir=${direction}):`,
                    err?.message
                );
                setChildData((prev) => ({
                    ...prev,
                    [key]: { loading: false, error: `Unable to load (${err?.message || "request failed"}).`, rows: [] },
                }));
            });
    };

    const toggleMaterials = (node) => {
        const willOpen = !expandedMat[node.path];
        setExpandedMat((current) => ({ ...current, [node.path]: willOpen }));
        if (willOpen) ensureChildren(node, "MATERIALS");
    };
    const toggleSites = (node) => {
        const willOpen = !expandedSites[node.path];
        setExpandedSites((current) => ({ ...current, [node.path]: willOpen }));
        if (willOpen) ensureChildren(node, "SITES");
    };

    return { toggleMaterials, toggleSites };
};

// Builds all the row-rendering helpers for the table body. Kept outside the
// component (and split into small single-purpose functions) so none of this
// branching counts toward DemandTable's own cognitive complexity.
const createRowRenderer = (ctx) => {
    const {
        fixedColumnOrder, visibleWeeks,
        levelChain, showSiteCode, expandedMat, expandedSites, childData,
        toggleMaterials, toggleSites,
    } = ctx;
    const hasBgColumn = fixedColumnOrder.includes("bg");

    const computeNodeFlags = (node, column) => {
        const matOpen = !!expandedMat[node.path];
        const sitesOpen = !!expandedSites[node.path];
        const hasNextMaterialLevel = levelChain.length
            ? !!resolveNextChainLevel(node, levelChain)
            : !!node.canMaterials;
        const canMat = canonicalLevel(node.level) !== "RMPM" && hasNextMaterialLevel;
        const canSites = showSiteCode == null && !!node.canSites && !node.siteCode;
        const inBg = column === "bg";
        const inBoth = column === "both";
        const hasCenteredSiteLabel = canSites && !!node.clusterName;
        return { matOpen, sitesOpen, canMat, canSites, inBg, inBoth, hasCenteredSiteLabel };
    };

    const chevronButton = (node, matOpen) => (
        <button
            className={styles.expandButton}
            type="button"
            onClick={() => toggleMaterials(node)}
            aria-label={`${matOpen ? "Collapse" : "Expand"} materials for ${node.primary}`}
        >
            <ChevronRight size={16} className={matOpen ? styles.expandedIcon : styles.expandIcon} />
        </button>
    );
    const plusButton = (node, sitesOpen) => (
        <button
            className={styles.addButton}
            type="button"
            onClick={() => toggleSites(node)}
            aria-label={`${sitesOpen ? "Collapse" : "Expand"} sites for ${node.primary}`}
        >
            {sitesOpen ? <Minus size={18} /> : <Plus size={18} />}
        </button>
    );

    // The "BG" cell for a row that is currently showing its own site drill-down
    // (i.e. a materials-drilled row that also has a site dimension to expand).
    const renderSiteCellForBgColumn = (node, canSites, sitesOpen) => {
        if (canSites) {
            return (
                <>
                    {plusButton(node, sitesOpen)}
                    {node.clusterName && (
                        <Box className={styles.textOverflowContainer}>
                            <Box className={styles.mainText} title={node.clusterName}>{node.clusterName}</Box>
                        </Box>
                    )}
                </>
            );
        }
        if (node.siteCode || node.clusterName) {
            const siteLabel = node.siteCode || node.clusterName;
            return (
                <Box className={styles.textOverflowContainer}>
                    <Box className={styles.mainText} title={siteLabel}>{siteLabel}</Box>
                </Box>
            );
        }
        return <span className={styles.noSites}>−</span>;
    };

    // The "SITE" cell for a row that has been drilled into via the site column
    // itself (rather than the BG/materials column).
    const renderSiteCellForSiteColumn = (node, canSites, sitesOpen) => (
        <>
            {canSites ? plusButton(node, sitesOpen) : <span className={styles.expandSpacer} />}
            <Box className={styles.textOverflowContainer}>
                <Box className={styles.mainText} title={node.primary}>{node.primary}</Box>
                {node.secondary && <Box className={styles.subText}>{node.secondary}</Box>}
            </Box>
        </>
    );

    const statusRow = (key, depth, column, content) => (
        <tr className={styles.childRow} key={key}>
            {renderFixedCells(fixedColumnOrder, STICKY_TD_CLASSES, (fixedKey, stickyClass) => (
                <td className={`${styles.textCell} ${stickyClass}`} key={fixedKey}>
                    {fixedKey === "bg" && column === "bg" && (
                        <Box
                            className={styles.bgContent}
                            sx={{ paddingLeft: `${depth * 14}px`, display: "flex", alignItems: "center", gap: 1 }}
                        >
                            {content}
                        </Box>
                    )}
                    {fixedKey === "site" && column === "site" && (
                        <Box
                            className={styles.siteContent}
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                            {content}
                        </Box>
                    )}
                </td>
            ))}
            {visibleWeeks.map((week) => (
                <td className={styles.weekCell} key={`${key}-${week}`} />
            ))}
        </tr>
    );

    const renderChildBlock = (node, direction, depth) => {
        const key = childKey(node.path, direction);
        const column = direction === "MATERIALS" ? "bg" : "site";
        const entry = childData[key];
        if (!entry || entry.loading) {
            return [
                statusRow(`${key}-loading`, depth, column, (
                    <>
                        <CircularProgress size={14} />
                        <span>Loading…</span>
                    </>
                )),
            ];
        }
        if (entry.error) {
            return [statusRow(`${key}-error`, depth, column, entry.error)];
        }
        if (!entry.rows.length) {
            return [statusRow(`${key}-empty`, depth, column, "No data.")];
        }
        return entry.rows.flatMap((child) => renderNode(child, depth, column));
    };

    const renderNodeRow = (node, depth, column, pad, flags) => {
        const { matOpen, canMat, canSites, inBg, inBoth, hasCenteredSiteLabel, sitesOpen } = flags;
        const bgLabel = inBg ? `${node.primary}${node.des ? ` - ${node.des}` : ""}` :
            `${node.label}${node.des ? ` - ${node.des}` : ""}`;
        return (
            <tr className={inBoth ? undefined : styles.childRow} key={node.path}>
                {renderFixedCells(fixedColumnOrder, STICKY_TD_CLASSES, (fixedKey, stickyClass) => (
                    <td className={`${styles.textCell} ${stickyClass}`} key={fixedKey}>
                        {fixedKey === "bg" && (inBg || inBoth || column === "site") && (
                            <Box className={styles.bgContent} sx={{ paddingLeft: pad }}>
                                {canMat ? chevronButton(node, matOpen) : <span className={styles.expandSpacer} />}
                                <span className={styles.bgDivider} />
                                <Box className={styles.textOverflowContainer}>
                                    <Box className={styles.mainText} title={bgLabel}>{bgLabel}</Box>
                                </Box>
                            </Box>
                        )}
                        {fixedKey === "site" && (
                            <Box
                                className={styles.siteContent}
                                sx={hasBgColumn && (inBg || inBoth) && !hasCenteredSiteLabel ? { justifyContent: "center" } : undefined}
                            >
                                {hasBgColumn && (inBg || inBoth)
                                    ? renderSiteCellForBgColumn(node, canSites, sitesOpen)
                                    : renderSiteCellForSiteColumn(node, canSites, sitesOpen)}
                            </Box>
                        )}
                        {fixedKey === "uom" && node.uom}
                    </td>
                ))}
                {visibleWeeks.map((week) => (
                    <td className={styles.weekCell} key={`${node.path}-${week}`}>
                        {formatNumber(node.weeklyData?.[week])}
                    </td>
                ))}
            </tr>
        );
    };

    const renderNode = (node, depth, column) => {
        const flags = computeNodeFlags(node, column);
        const pad = `${depth * 14}px`;
        const out = [renderNodeRow(node, depth, column, pad, flags)];

        if (flags.sitesOpen) out.push(...renderChildBlock(node, "SITES", depth + 1));
        if (flags.matOpen) out.push(...renderChildBlock(node, "MATERIALS", depth + 1));
        return out;
    };

    return { renderNode };
};

function DemandTable({ filters = {}, sublevelFilters = [], clearFilters = false, columnPreferences = [], columnPreferencesReady = true, showSiteCode = null, uom = "EA" }) {
    const filtersKey = JSON.stringify(filters || {});
    const sublevelKey = JSON.stringify(sublevelFilters || []);
    const clearFiltersKey = String(clearFilters);
    const customizationKey = useMemo(() => JSON.stringify(columnPreferences || []), [columnPreferences]);

    const {
        apiPayload, loading, error,
        page, setPage,
        expandedMat, setExpandedMat,
        expandedSites, setExpandedSites,
        childData, setChildData
    } = useDemandData({ filtersKey, sublevelKey, clearFiltersKey, clearFilters, showSiteCode, customizationKey, uom, columnPreferencesReady });

    const [fontsReady, setFontsReady] = useState(false);
    useEffect(() => {
        if (typeof document === "undefined" || !document.fonts) return undefined;
        let cancelled = false;
        document.fonts.ready.then(() => {
            if (!cancelled) setFontsReady(true);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const levelChain = useMemo(() => {
        const sf = JSON.parse(sublevelKey);
        return sf.length ? buildLevelChain(sf) : [];
    }, [sublevelKey]);

    const { toggleMaterials, toggleSites } = useChildExpansion({
        filtersKey, sublevelKey, clearFilters, showSiteCode, levelChain,
        expandedMat, setExpandedMat, expandedSites, setExpandedSites,
        childData, setChildData, uom,
    });

    const weeks = useMemo(() => deriveWeeks(apiPayload?.weekly_total), [apiPayload]);
    const weeklyTotal = apiPayload?.weekly_total || {};

    const { showDemand, fixedColumnOrder, visibleWeeks } = useMemo(
        () => getColumnVisibility(columnPreferences, weeks),
        [columnPreferences, weeks]
    );

    const totalRecords = apiPayload?.totalRecords ?? 0;
    // Keep Next enabled when QA returns a full page but an incorrect total equal
    // to the page size. MUI uses -1 for an unknown server-side total.
    const paginationCount = apiPayload?.hasMore && totalRecords <= page * PAGE_SIZE
        ? -1
        : totalRecords;

    const nodes = useMemo(() => {
        const rows = apiPayload?.rows || [];
        const opts = apiPayload?.expansionOptions?.length
            ? apiPayload.expansionOptions
            : ["SITES", "MATERIALS"];
        return rowsToNodes(rows, opts, 0, showSiteCode, null);
    }, [apiPayload, showSiteCode]);

    const bgColWidth = useMemo(
        () => computeBgColumnWidth(nodes, expandedMat, expandedSites, childData, fontsReady),
        [nodes, expandedMat, expandedSites, childData, fontsReady]
    );

    const { col1Width, col2Width, col3Width, frozenWidth, tableMinWidth } = useMemo(
        () => computeStickyLayout(fixedColumnOrder, bgColWidth, visibleWeeks),
        [fixedColumnOrder, bgColWidth, visibleWeeks]
    );

    useEffect(() => {
        exportCache = {
            weeks,
            nodes,
            weeklyTotal,
            filters: JSON.parse(filtersKey),
            sublevelFilters: JSON.parse(sublevelKey),
            totalRecords,
            showSiteCode,
        };
    }, [weeks, nodes, weeklyTotal, filtersKey, sublevelKey, totalRecords, showSiteCode]);

    const { renderNode } = useMemo(
        () => createRowRenderer({
            fixedColumnOrder, visibleWeeks,
            levelChain, showSiteCode, expandedMat, expandedSites, childData,
            toggleMaterials, toggleSites,
        }),
        [
            fixedColumnOrder, visibleWeeks,
            levelChain, showSiteCode, expandedMat, expandedSites, childData,
            toggleMaterials, toggleSites,
        ]
    );

    if (loading) {
        return (
            <Box className={styles.stateShell}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    if (error) {
        return <Box className={styles.stateShell}>{error}</Box>;
    }
    if (!nodes.length) {
        return <Box className={styles.stateShell}>No demand data available.</Box>;
    }

    return (
        <>
            <StickyScrollShell frozenWidth={frozenWidth}>
                <table
                    className={styles.table}
                    style={{
                        "--col1-width": `${col1Width}px`,
                        "--col2-width": `${col2Width}px`,
                        "--col3-width": `${col3Width}px`,
                        "--table-min-width": `${tableMinWidth}px`,
                    }}
                >
                    <thead>
                        <tr>
                            {renderFixedCells(fixedColumnOrder, STICKY_TH_CLASSES, (key, stickyClass) => (
                                <th
                                    className={`${styles.primaryHeader} ${stickyClass}`}
                                    rowSpan={2}
                                    key={key}
                                >
                                    {FIXED_COLUMN_LABEL[key]}
                                </th>
                            ))}
                            {showDemand && visibleWeeks.length > 0 && <th className={styles.groupHeader} colSpan={visibleWeeks.length}>
                                Demand
                            </th>}
                        </tr>
                        <tr>
                            {visibleWeeks.map((week) => (
                                <th
                                    className={`${styles.groupHeader} ${styles.weekHeader}`}
                                    key={week}
                                >
                                    {weekLabel(week)}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        <tr className={styles.totalRow}>
                            {renderFixedCells(fixedColumnOrder, STICKY_TD_CLASSES, (key, stickyClass) => (
                                <td className={stickyClass} key={key}>
                                    {key === "bg" && (
                                        <Box className={styles.bgContent}>
                                            <span className={styles.expandSpacer} />
                                            <span className={styles.bgDivider} />
                                        </Box>
                                    )}
                                </td>
                            ))}
                            {visibleWeeks.map((week) => (
                                <td key={`total-${week}`}>
                                    {formatNumber(weeklyTotal[week])}
                                </td>
                            ))}
                        </tr>

                        {nodes.flatMap((node) => renderNode(node, 0, "both"))}
                    </tbody>
                </table>
            </StickyScrollShell>
            <TablePagination
                className={styles.pagination}
                component="div"
                count={paginationCount}
                page={page - 1}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[]}
                onPageChange={(_, nextPage) => setPage(nextPage + 1)}
                labelDisplayedRows={({ from, to, count }) =>
                    (count === -1 ? `${from} – ${to}` : `${from} – ${to} of ${count}`)
                }
                sx={{
                    "& .MuiTablePagination-toolbar": { minHeight: 30, marginTop: '10px', },
                    "& .MuiTablePagination-displayedRows": { fontSize: 14, margin: 0 },
                    "& .MuiTablePagination-actions": { marginLeft: 2, paddingBottom: 0 },
                    "& .MuiIconButton-root": { padding: 0 },
                }}
            />
        </>
    );
}

DemandTable.propTypes = {
    filters: PropTypes.object,
    sublevelFilters: PropTypes.arrayOf(PropTypes.string),
    clearFilters: PropTypes.bool,
    columnPreferences: PropTypes.arrayOf(PropTypes.object),
    columnPreferencesReady: PropTypes.bool,
    showSiteCode: PropTypes.bool,
    uom: PropTypes.string,
};

export const exportDemandTable = async () => {
    const { nodes, filters, sublevelFilters, showSiteCode } = exportCache;
    if (!nodes.length) return false;

    let exportWeeks;
    let exportNodes;
    try {
        const pagePayloads = showSiteCode == null
            ? await fetchAllBGPages({ filters, sublevelFilters })
            : await fetchAllBGPages(
                { filters, sublevelFilters, parent: null, showSiteCode },
                fetchDemandPageSite
            );
        exportWeeks = deriveWeeks(pagePayloads[0]?.weekly_total);
        exportNodes = pagePayloads.flatMap((data) => {
            const options = data.expansionOptions?.length ? data.expansionOptions : ["SITES", "MATERIALS"];
            return rowsToNodes(data.rows, options, 0, showSiteCode, null);
        });
    } catch (error) {
        console.error("Unable to export all demand pages:", error.message);
        return false;
    }

    const headers = ["BG", "Site Code", "UOM", ...exportWeeks.map(weekLabel)];
    const exportRows = exportNodes.map((node) => [
        node.label,
        node.clusterName,
        node.uom,
        ...exportWeeks.map((week) => formatForExport(node.weeklyData?.[week])),
    ]);

    const currentDate = new Date();
    const formattedCurrentDate = [
        String(currentDate.getDate()).padStart(2, "0"),
        String(currentDate.getMonth() + 1).padStart(2, "0"),
        String(currentDate.getFullYear()),
    ].join("-");

    downloadXls(headers, exportRows, `Demand_RM Analytics_${formattedCurrentDate}.xls`);
    return true;
};

export default DemandTable;